/**
 * Cross-platform image picker utility
 * Works on both web and mobile platforms
 */
import * as ImagePicker from 'expo-image-picker';
import { Alert, Platform } from 'react-native';
import { toast } from '@/components/ui/toast';
import { isRawAsset } from '@/utils/image-format';
import { MAX_IMAGE_DIMENSION, normalizeToJpeg } from '@/utils/image-normalize';
import { isAcceptedImageFile, MAX_FILE_SIZE } from '@/utils/photo-validation';
import type { CheckInCropResult } from '@/utils/show-check-in-crop-modal';

/** `accept` list for the hidden web file input. HEIC/HEIF come from iPhones. */
export const WEB_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

/** JPEG quality for web re-encodes (crop output and skip-crop resize). */
const WEB_JPEG_QUALITY = 0.9;

/**
 * Release `blob:` object URLs created for web previews (picked, resized or
 * cropped images). Other URIs are ignored, so this is safe to call with any
 * photo URIs on any platform; on native there are no blob URLs and it does
 * nothing.
 */
export function revokeBlobUris(uris: readonly string[]): void {
  const revoke = typeof URL !== 'undefined' ? URL.revokeObjectURL : undefined;
  if (typeof revoke !== 'function') return;
  for (const uri of uris) {
    if (!uri || !uri.startsWith('blob:')) continue;
    try {
      URL.revokeObjectURL(uri);
    } catch {
      // Already revoked or not ours; nothing else to release.
    }
  }
}

export const PICKER_CONNECTION_ERROR_MESSAGE = 'Check your connection and try again.';
export const PICKER_PROCESSING_ERROR_MESSAGE =
  'The photo could not be processed. Try again or choose a different photo.';

/**
 * Failures that come from the network rather than from the image: on web the
 * crop modals are lazy chunks, so going offline makes `import()` reject.
 */
const CONNECTION_ERROR_PATTERN =
  /failed to fetch|network ?(request|error)|load failed|loading (css )?chunk|dynamically imported module|importing a module script failed/i;

/** Errors this module throws on purpose carry a message meant for the user. */
const USER_FACING_ERROR_PATTERN = /permission/i;

/**
 * Turn an error from picking or preparing an image into a message for the
 * user: a connection problem, a permission problem (message kept), or a
 * processing failure (decode, resize, crop).
 */
export function describePickerError(error: unknown): string {
  const message = error instanceof Error ? error.message : '';
  const name = error instanceof Error ? error.name : '';
  if (name === 'ChunkLoadError' || CONNECTION_ERROR_PATTERN.test(message)) {
    return PICKER_CONNECTION_ERROR_MESSAGE;
  }
  if (USER_FACING_ERROR_PATTERN.test(message)) {
    return message;
  }
  return PICKER_PROCESSING_ERROR_MESSAGE;
}

export interface ImagePickerResult {
  uri: string;
  type?: string;
  name?: string;
  /** Byte size of the picked (and, on native, normalized) image when known. */
  size?: number;
  cancelled: boolean;
  /** Original File reference (web only). Retained to avoid stale blob URL re-fetches. */
  file?: File;
}

/**
 * Post-process a native picker asset into an upload-ready result.
 *
 * RAW captures are passed through untouched (carrying their original name/mime)
 * so the validation preflight can reject them with a clear message; the image
 * pipeline cannot decode them. Everything else is normalized to a bounded JPEG.
 */
async function buildResultFromMobileAsset(
  asset: ImagePicker.ImagePickerAsset
): Promise<ImagePickerResult> {
  const name = asset.fileName ?? 'image.jpg';

  if (isRawAsset({ mimeType: asset.mimeType, fileName: asset.fileName })) {
    return {
      uri: asset.uri,
      type: asset.mimeType,
      name,
      size: asset.fileSize,
      cancelled: false,
    };
  }

  const normalized = await normalizeToJpeg(asset.uri, {
    width: asset.width,
    height: asset.height,
  });

  return {
    uri: normalized.uri,
    type: normalized.mimeType,
    name,
    size: normalized.size,
    cancelled: false,
  };
}

export interface ImagePickerOptions {
  allowsEditing?: boolean;
  aspect?: [number, number];
  quality?: number;
  /** Web only: show advanced crop modal with free-form crop, rotation, and skip option */
  showAdvancedCrop?: boolean;
}

/**
 * Pick an image from the gallery/file system
 */
export async function pickImageFromGallery(options: ImagePickerOptions = {}): Promise<ImagePickerResult> {
  const {
    allowsEditing = true,
    aspect = [1, 1],
    quality = 0.8,
    showAdvancedCrop,
  } = options;

  if (Platform.OS === 'web') {
    return pickImageFromWeb(allowsEditing ? aspect : undefined, showAdvancedCrop);
  } else {
    // Mobile implementation
    const { status: libraryStatus } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (libraryStatus !== 'granted') {
      throw new Error('Photo library permission is required to select images.');
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing,
      ...(allowsEditing && aspect ? { aspect } : {}),
      quality,
    });

    if (result.canceled || !result.assets || result.assets.length === 0) {
      return { uri: '', cancelled: true };
    }

    return buildResultFromMobileAsset(result.assets[0]);
  }
}

export interface MultiImagePickerOptions {
  /** Maximum number of images to return (remaining slots). 0 returns [] without opening a picker. */
  selectionLimit: number;
  quality?: number;
  /** Web only: show the advanced crop modal for each selected image. */
  showAdvancedCrop?: boolean;
}

/**
 * Pick one or more images from the gallery/file system, never more than
 * `selectionLimit`. Native: multi-select picker (the limit is enforced by the
 * OS picker on iOS 14+ and Android, and again here). Web: a `multiple` file
 * input, each file cropped (or skipped and resized) in turn.
 * Returns an empty list when the user cancels.
 */
export async function pickImagesFromGallery(
  options: MultiImagePickerOptions
): Promise<ImagePickerResult[]> {
  const { selectionLimit, quality = 0.8, showAdvancedCrop } = options;
  if (selectionLimit <= 0) return [];

  if (Platform.OS === 'web') {
    const files = await selectFilesFromWeb(selectionLimit > 1);
    return prepareWebImageFiles(files, { limit: selectionLimit, showAdvancedCrop });
  }

  const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (status !== 'granted') {
    throw new Error('Photo library permission is required to select images.');
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    allowsEditing: false,
    allowsMultipleSelection: selectionLimit > 1,
    selectionLimit,
    orderedSelection: true,
    quality,
  });

  if (result.canceled || !result.assets || result.assets.length === 0) {
    return [];
  }

  return Promise.all(result.assets.slice(0, selectionLimit).map(buildResultFromMobileAsset));
}

/**
 * Take a photo using the camera
 */
export async function takePhotoWithCamera(options: ImagePickerOptions = {}): Promise<ImagePickerResult> {
  const {
    allowsEditing = true,
    aspect = [1, 1],
    quality = 0.8,
    showAdvancedCrop,
  } = options;

  if (Platform.OS === 'web') {
    // On web, fallback to file picker since camera access is complex
    console.log('Camera not available on web, falling back to file picker');
    return pickImageFromWeb(allowsEditing ? aspect : undefined, showAdvancedCrop);
  } else {
    // Mobile implementation
    const { status: cameraStatus } = await ImagePicker.requestCameraPermissionsAsync();
    if (cameraStatus !== 'granted') {
      throw new Error('Camera permission is required to take photos.');
    }

    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ['images'],
      allowsEditing,
      ...(allowsEditing && aspect ? { aspect } : {}),
      quality,
    });

    if (result.canceled || !result.assets || result.assets.length === 0) {
      return { uri: '', cancelled: true };
    }

    return buildResultFromMobileAsset(result.assets[0]);
  }
}

/**
 * Web-specific single image picker using HTML file input + crop modal
 */
async function pickImageFromWeb(
  aspect?: [number, number],
  showAdvancedCrop?: boolean
): Promise<ImagePickerResult> {
  const [file] = filterWebImageFiles(await selectFilesFromWeb(false), 1);
  if (!file) {
    return { uri: '', cancelled: true };
  }

  // Advanced crop modal (check-in photos): free-form, rotation, skip option
  if (showAdvancedCrop) {
    const [prepared] = await prepareWebImageFiles([file], { limit: 1, showAdvancedCrop: true });
    return prepared ?? { uri: '', cancelled: true };
  }

  const rawResult = toWebResult(file, URL.createObjectURL(file));

  // Skip crop modal when no aspect ratio is specified (allowsEditing: false)
  if (!aspect) {
    return rawResult;
  }

  // Dynamically import to avoid loading react-easy-crop at module parse time
  const { showCropModal } = await import('@/utils/show-crop-modal');
  const croppedUri = await showCropModal(rawResult.uri, aspect[0] / aspect[1]);

  // Revoke the original blob URI regardless of outcome
  URL.revokeObjectURL(rawResult.uri);

  if (!croppedUri) {
    return { uri: '', cancelled: true };
  }

  return croppedWebResult(croppedUri, file.name);
}

/** Replace a file name's extension with `.jpg` (cropped and resized output is JPEG). */
function toJpegName(name: string | undefined): string {
  const base = (name || 'photo').replace(/\.[^./]+$/, '');
  return `${base}.jpg`;
}

/** Browsers often report HEIC with an empty type; infer it from the extension. */
function inferMimeType(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.toLowerCase().split('.').pop();
  if (extension === 'heic' || extension === 'heif') return `image/${extension}`;
  return 'image/jpeg';
}

function toWebResult(file: File, uri: string): ImagePickerResult {
  return {
    uri,
    type: inferMimeType(file),
    name: file.name,
    size: file.size,
    cancelled: false,
    file,
  };
}

/**
 * Turn a cropped blob URL into a result backed by a stable File. Cropped
 * output is always JPEG, whatever the source format was.
 */
async function croppedWebResult(blobUri: string, sourceName: string): Promise<ImagePickerResult> {
  const name = toJpegName(sourceName);
  let file: File | undefined;
  try {
    const response = await fetch(blobUri);
    const blob = await response.blob();
    file = new File([blob], name, { type: 'image/jpeg' });
  } catch {
    // Fallback: upload path will re-fetch the blob URL if needed
  }

  return {
    uri: blobUri,
    type: 'image/jpeg',
    name,
    size: file?.size,
    cancelled: false,
    file,
  };
}

/** Load an image element; resolves null when the browser cannot decode it. */
function loadWebImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', WEB_JPEG_QUALITY);
  });
}

/**
 * Downscale an image so its longest edge is at most `maxEdge`, re-encoded as
 * JPEG. Returns the original file when it already fits, when the browser
 * cannot decode it (for example HEIC outside Safari), or on any canvas error:
 * the server resizes as a backstop.
 */
export async function resizeImageFileForWeb(
  file: File,
  maxEdge: number = MAX_IMAGE_DIMENSION
): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadWebImage(url);
    if (!image) return file;

    const { naturalWidth: width, naturalHeight: height } = image;
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    if (scale >= 1) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext('2d');
    if (!context) return file;

    // JPEG has no alpha channel; paint transparent PNG areas white, not black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToJpeg(canvas);
    if (!blob) return file;
    return new File([blob], toJpegName(file.name), { type: 'image/jpeg' });
  } catch (error) {
    console.warn('Image resize failed, uploading the original:', error);
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Prepare one browser File: crop (or skip-and-resize) through the advanced
 * crop modal. Returns null when the user cancels the crop.
 */
async function prepareWebImageFile(
  file: File,
  showAdvancedCrop: boolean | undefined,
  openCropModal: CropModalLauncher
): Promise<ImagePickerResult | null> {
  const objectUrl = URL.createObjectURL(file);
  // The object URL is handed to the caller as the preview only on the paths
  // that return `toWebResult(file, objectUrl)`; every other exit, including a
  // throw from the cropper or resize, releases it.
  let keepObjectUrl = false;
  try {
    if (!showAdvancedCrop) {
      keepObjectUrl = true;
      return toWebResult(file, objectUrl);
    }

    // The cropper cannot show an image the browser cannot decode (HEIC on
    // Chrome/Firefox); send those as-is and let the server convert them.
    const decodable = await loadWebImage(objectUrl);
    if (!decodable) {
      keepObjectUrl = true;
      return toWebResult(file, objectUrl);
    }

    const crop = await openCropModal(objectUrl);
    if (crop.action === 'cancelled') return null;
    if (crop.action === 'cropped') return croppedWebResult(crop.blobUri, file.name);

    // Skipped: keep the framing but still cap the pixel size.
    const resized = await resizeImageFileForWeb(file);
    if (resized === file) {
      keepObjectUrl = true;
      return toWebResult(file, objectUrl);
    }
    return toWebResult(resized, URL.createObjectURL(resized));
  } finally {
    if (!keepObjectUrl) URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Keep the supported image files, at most `limit` of them, warning about the
 * ones skipped (unsupported type, over 10MB, or over the limit).
 */
function filterWebImageFiles(files: readonly File[], limit: number): File[] {
  const images = files.filter(isAcceptedImageFile);
  if (images.length < files.length) {
    toast.warning('File type not supported', {
      description: 'Some files were skipped. Use JPEG, PNG, WebP or HEIC images.',
    });
  }

  const sized = images.filter((file) => file.size <= MAX_FILE_SIZE);
  if (sized.length < images.length) {
    toast.warning('File too large', { description: 'Please select an image smaller than 10MB.' });
  }

  const accepted = sized.slice(0, Math.max(0, limit));
  if (accepted.length < sized.length && limit <= 0) {
    toast.warning('Maximum photos reached', { description: 'Remove a photo to add another.' });
  } else if (accepted.length < sized.length) {
    toast.warning(`You can add ${limit} more photo${limit === 1 ? '' : 's'}`, {
      description: `Only the first ${limit} ${limit === 1 ? 'was' : 'were'} added.`,
    });
  }
  return accepted;
}

export type CropModalLauncher = (imageSrc: string) => Promise<CheckInCropResult>;

/** Lazily load the cropper so react-advanced-cropper stays out of the main bundle. */
const openCheckInCropModal: CropModalLauncher = async (imageSrc) => {
  const { showCheckInCropModal } = await import('@/utils/show-check-in-crop-modal');
  return showCheckInCropModal(imageSrc);
};

export interface PrepareWebImageFilesOptions {
  /** Maximum number of images to keep. */
  limit: number;
  showAdvancedCrop?: boolean;
  /** Crop modal launcher; defaults to the lazily loaded check-in crop modal. */
  openCropModal?: CropModalLauncher;
}

/**
 * Turn picked, dropped or pasted browser files into upload-ready results.
 * Unsupported and oversized files are skipped with a warning, the list is cut
 * to `limit`, and each remaining file goes through the crop modal in turn.
 */
export async function prepareWebImageFiles(
  files: readonly File[],
  { limit, showAdvancedCrop, openCropModal = openCheckInCropModal }: PrepareWebImageFilesOptions
): Promise<ImagePickerResult[]> {
  const accepted = filterWebImageFiles(files, limit);

  const results: ImagePickerResult[] = [];
  try {
    for (const file of accepted) {
      // Sequential on purpose: the crop modal shows one image at a time.
      const prepared = await prepareWebImageFile(file, showAdvancedCrop, openCropModal);
      if (prepared) results.push(prepared);
    }
  } catch (error) {
    // The caller never sees these results, so their previews would leak.
    revokeBlobUris(results.map((result) => result.uri));
    throw error;
  }
  return results;
}

/**
 * Low-level file selection via HTML file input (no cropping).
 * Resolves an empty list when the user cancels.
 */
function selectFilesFromWeb(multiple: boolean): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = WEB_IMAGE_ACCEPT;
    input.multiple = multiple;
    input.style.display = 'none';

    const finish = (files: File[]) => {
      if (input.parentNode) input.parentNode.removeChild(input);
      resolve(files);
    };

    input.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      finish(target.files ? Array.from(target.files) : []);
    };

    input.oncancel = () => finish([]);

    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Show image picker options (Camera or Gallery)
 */
export function showImagePickerOptions(
  onCameraPress: () => void,
  onGalleryPress: () => void,
  onCancel?: () => void
): void {
  if (Platform.OS === 'web') {
    // On web, just show gallery picker since camera is complex
    onGalleryPress();
  } else {
    // On mobile, show the native alert
    Alert.alert(
      'Select Image',
      'Choose how you want to select your profile image',
      [
        {
          text: 'Camera',
          onPress: onCameraPress,
        },
        {
          text: 'Gallery',
          onPress: onGalleryPress,
        },
        {
          text: 'Cancel',
          style: 'cancel',
          onPress: onCancel,
        },
      ],
      { cancelable: true }
    );
  }
}
